/**
 * Desktop (Web) Notifications helper.
 *
 * Thin wrapper around the browser Notification API so the rest of the app
 * doesn't have to worry about feature detection, permission state, or the
 * user-gesture constraint on requestPermission().
 */

export function isDesktopNotificationSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

/**
 * Idempotent permission request. Only prompts when the current state is
 * 'default' — never nags a user who already decided. Must be called from a
 * user-gesture handler (e.g. a button click) for the browser to show the
 * prompt.
 */
export async function requestDesktopNotificationPermission(): Promise<void> {
  if (!isDesktopNotificationSupported()) return;
  if (Notification.permission !== 'default') return;
  try {
    await Notification.requestPermission();
  } catch {
    // Some browsers reject when called outside a user gesture. Fail silently.
  }
}

interface FireOptions {
  title: string;
  body: string;
  icon?: string;
  /** Tag ensures a new notification replaces an older one for the same subject. */
  tag?: string;
  onClick?: () => void;
}

/**
 * Fires a notification only when permission is granted and the tab is hidden.
 * If the tab is currently visible, the in-app Success-Dialog is a better UX,
 * so we no-op.
 */
export function fireDesktopNotification({ title, body, icon, tag, onClick }: FireOptions): void {
  if (!isDesktopNotificationSupported()) return;
  if (Notification.permission !== 'granted') return;
  if (document.visibilityState !== 'hidden') return;

  const notification = new Notification(title, { body, icon, tag });

  notification.onclick = () => {
    window.focus();
    notification.close();
    onClick?.();
  };
}
